import React from 'react';
import {
  Avatar, Box, Chip, Dialog, DialogContent,
  Divider, IconButton, Stack, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Close, LocationOn, Phone } from '@mui/icons-material';
import { format } from 'date-fns';
import { ACTIVITY_STAGES, STATUS_CONFIG } from '../constants';
import { getStatus } from '../utils';
import ActivityStageBlock from './ActivityStageBlock';

/**
 * Full-detail modal for a single user's activity record.
 * Shows their timeline, photos, and last known location.
 *
 * Props:
 *   row     – activity row object
 *   open    – boolean
 *   onClose – close handler
 */
export default function UserModal({ row, open, onClose }) {
  if (!row) return null;

  const { author, checkin, project, region } = row;
  const status    = getStatus(row);
  const statusCfg = STATUS_CONFIG[status];
  const activity  = row.currentLocation || checkin;

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: '98vw', sm: '90vw', md: '820px' },
          maxWidth: '900px', maxHeight: '90vh',
          borderRadius: '20px', overflow: 'hidden',
          background: '#ffffff',
          boxShadow: '0 24px 64px rgba(15,33,55,0.18), 0 8px 24px rgba(15,33,55,0.1)',
        },
      }}
    >
      <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>

        {/* ── Header ── */}
        <Box sx={{
          position: 'relative', flexShrink: 0,
          background: `linear-gradient(135deg, ${alpha(statusCfg.color, 0.12)} 0%, ${alpha('#1075BA', 0.06)} 100%)`,
          borderBottom: `1px solid ${alpha(statusCfg.color, 0.15)}`,
          p: 3,
        }}>
          {/* Decorative radial glow */}
          <Box sx={{
            position: 'absolute', right: -40, top: -40,
            width: 200, height: 200, borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha(statusCfg.color, 0.1)}, transparent 70%)`,
            pointerEvents: 'none',
          }} />

          <IconButton
            onClick={onClose} size="small"
            sx={{ position: 'absolute', top: 14, right: 14, color: '#4a6a85', '&:hover': { background: 'rgba(15,33,55,0.07)' } }}
          >
            <Close fontSize="small" />
          </IconButton>

          <Stack direction="row" spacing={2.5} alignItems="center">
            {/* Avatar */}
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Avatar
                src={author?.photoURL}
                sx={{
                  width: 80, height: 80,
                  border: `3px solid ${statusCfg.color}`,
                  boxShadow: `0 0 0 4px ${alpha(statusCfg.color, 0.2)}, 0 6px 20px rgba(15,33,55,0.15)`,
                  fontSize: '1.5rem', fontWeight: 700,
                }}
              >
                {author?.firstName?.[0]}
              </Avatar>
              <Box sx={{
                position: 'absolute', bottom: 2, right: 2,
                width: 16, height: 16, borderRadius: '50%',
                background: statusCfg.color, border: '2.5px solid #fff',
                boxShadow: `0 0 8px ${alpha(statusCfg.color, 0.6)}`,
              }} />
            </Box>

            {/* Name + badges */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f2137', lineHeight: 1.2, mb: 0.3 }}>
                {author?.firstName} {author?.lastName}
              </Typography>
              <Typography sx={{ color: '#4a6a85', fontSize: '0.85rem', mb: 1 }}>
                @{author?.username} · {author?.role?.title || '—'}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip
                  label={statusCfg.label} size="small"
                  sx={{ background: statusCfg.bg, color: statusCfg.color, border: `1px solid ${statusCfg.border}`, fontWeight: 700, fontSize: '0.72rem', height: 24 }}
                />
                {project?.title && (
                  <Chip label={project.title} size="small"
                    sx={{ background: '#e6f2ff', color: '#1075BA', border: '1px solid #99ccf5', fontSize: '0.72rem', height: 24 }} />
                )}
                {(region?.title || author?.region?.title) && (
                  <Chip label={region?.title || author?.region?.title} size="small"
                    sx={{ background: '#f0f4f7', color: '#4a6a85', border: '1px solid #c8d4dc', fontSize: '0.72rem', height: 24 }} />
                )}
              </Stack>
            </Box>

            {/* Mobile + coords */}
            <Box sx={{ flexShrink: 0, textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
              {author?.mobileNumber && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'flex-end', mb: 0.5 }}>
                  <Phone sx={{ fontSize: 14, color: '#4a6a85' }} />
                  <Typography sx={{ fontSize: '0.82rem', color: '#0f2137' }}>{author.mobileNumber}</Typography>
                </Box>
              )}
              {activity && activity !== 'NA' && activity.lat && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'flex-end' }}>
                  <LocationOn sx={{ fontSize: 14, color: '#4a6a85' }} />
                  <Typography sx={{ fontSize: '0.78rem', color: '#4a6a85', fontFamily: 'monospace' }}>
                    {parseFloat(activity.lat).toFixed(5)}, {parseFloat(activity.long).toFixed(5)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
        </Box>

        {/* ── Body ── */}
        <Box sx={{ overflowY: 'auto', flex: 1, p: 3 }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#1075BA', mb: 2 }}>
            Activity Timeline & Photos
          </Typography>

          <Stack spacing={1.5}>
            {ACTIVITY_STAGES.map((stage) => (
              <ActivityStageBlock
                key={stage.key}
                stageKey={stage.key}
                label={stage.label}
                color={stage.color}
                data={row[stage.key]}
              />
            ))}
          </Stack>

          {/* Last known location */}
          {activity && activity !== 'NA' && activity.lat && (
            <>
              <Divider sx={{ my: 2.5, borderColor: 'rgba(16,117,186,0.1)' }} />
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#1075BA', mb: 1.5 }}>
                Last Known Location
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {[
                  { l: 'Latitude',  v: activity.lat },
                  { l: 'Longitude', v: activity.long },
                  { l: 'Reported',  v: activity.timeReported ? format(new Date(activity.timeReported), "MMM d 'at' hh:mm a") : '—' },
                  ...(activity.notes ? [{ l: 'Notes', v: activity.notes }] : []),
                ].map(({ l, v }) => (
                  <Box key={l} sx={{ background: '#f5f8fc', borderRadius: '10px', px: 2, py: 1.25, border: '1px solid rgba(16,117,186,0.1)' }}>
                    <Typography sx={{ fontSize: '0.68rem', color: '#4a6a85', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>
                      {l}
                    </Typography>
                    <Typography sx={{ fontSize: '0.83rem', color: '#0f2137', fontFamily: (l === 'Latitude' || l === 'Longitude') ? 'monospace' : 'inherit' }}>
                      {v}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
